import React, { useState, useEffect } from 'react';
import {
  Shield, Loader2, QrCode, Check, X, Copy, Users, Plus, Trash2, ScrollText,
  KeyRound, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Onglet "Sécurité" : double authentification (2FA/TOTP), gestion de l'équipe
 * (comptes admin additionnels), et journal d'audit (actions RGPD sensibles).
 */
export const AdminSecurityPanel = ({ csrfToken, onLogout, fmtDate }) => {
  const fetchWithCredentials = (url) => fetch(url, { credentials: 'include' });
  const fetchWithCsrf = (url, options = {}) => fetch(url, {
    ...options, credentials: 'include',
    headers: { ...options.headers, 'X-CSRF-Token': csrfToken, 'Content-Type': 'application/json' },
  });

  // ---------------- 2FA ----------------
  const [twoFaEnabled, setTwoFaEnabled] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [enableCode, setEnableCode] = useState('');
  const [enabling, setEnabling] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisable, setShowDisable] = useState(false);
  const [disabling, setDisabling] = useState(false);

  const load2fa = () => fetchWithCredentials(`${BACKEND_URL}/api/admin/security/2fa/status`)
    .then((r) => r.json()).then((d) => setTwoFaEnabled(d.enabled));

  useEffect(() => { load2fa(); }, []);

  const startSetup = async () => {
    const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/security/2fa/setup`, { method: 'POST', body: '{}' });
    const data = await res.json();
    setSetupData(data);
  };

  const confirmEnable = async () => {
    setEnabling(true);
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/security/2fa/enable`, { method: 'POST', body: JSON.stringify({ code: enableCode }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Code invalide');
      setBackupCodes(data.backup_codes);
      setSetupData(null);
      setEnableCode('');
      setTwoFaEnabled(true);
      toast.success('2FA activée avec succès.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setEnabling(false);
    }
  };

  const confirmDisable = async () => {
    setDisabling(true);
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/security/2fa/disable`, { method: 'POST', body: JSON.stringify({ password: disablePassword }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      setTwoFaEnabled(false);
      setShowDisable(false);
      setDisablePassword('');
      toast.success('2FA désactivée.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDisabling(false);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success('Codes copiés dans le presse-papier.');
  };

  // ---------------- Équipe ----------------
  const [team, setTeam] = useState(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember] = useState({ email: '', name: '', password: '' });
  const [addingMember, setAddingMember] = useState(false);

  const loadTeam = () => fetchWithCredentials(`${BACKEND_URL}/api/admin/security/team`)
    .then((r) => r.json()).then((d) => setTeam(d.members || []));

  useEffect(() => { loadTeam(); }, []);

  const addMember = async () => {
    if (!newMember.email || newMember.password.length < 8) {
      toast.error('Email requis + mot de passe de 8 caractères minimum.');
      return;
    }
    setAddingMember(true);
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/security/team`, { method: 'POST', body: JSON.stringify(newMember) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      toast.success(`Compte créé pour ${newMember.email}`);
      setShowAddMember(false);
      setNewMember({ email: '', name: '', password: '' });
      loadTeam();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAddingMember(false);
    }
  };

  const removeMember = async (email) => {
    if (!window.confirm(`Supprimer le compte admin ${email} ?`)) return;
    const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/security/team/${encodeURIComponent(email)}`, { method: 'DELETE' });
    const data = await res.json();
    if (!res.ok) { toast.error(data.detail || 'Erreur'); return; }
    toast.success('Compte supprimé.');
    loadTeam();
  };

  // ---------------- Journal d'audit ----------------
  const [auditLog, setAuditLog] = useState(null);

  useEffect(() => {
    fetchWithCredentials(`${BACKEND_URL}/api/admin/audit-log?limit=50`)
      .then((r) => (r.ok ? r.json() : { items: [] })).then((d) => setAuditLog(d.items || [])).catch(() => setAuditLog([]));
  }, []);

  return (
    <div data-testid="admin-security-panel" className="space-y-6">
      {/* 2FA */}
      <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5">
        <h3 className="flex items-center gap-2 font-bold text-white mb-1"><Shield className="w-5 h-5 text-primary" /> Double authentification (2FA)</h3>
        <p className="text-zinc-500 text-sm mb-4">Protège votre compte même si votre mot de passe est compromis. Utilisez Google Authenticator, Authy ou 1Password.</p>

        {twoFaEnabled === null ? (
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        ) : backupCodes ? (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4" data-testid="backup-codes-display">
            <h4 className="flex items-center gap-2 text-amber-300 font-bold text-sm mb-2"><AlertTriangle className="w-4 h-4" /> Codes de secours — à sauvegarder maintenant</h4>
            <p className="text-zinc-400 text-xs mb-3">Ces 8 codes à usage unique permettent de vous reconnecter si vous perdez votre téléphone. Ils ne seront plus affichés après.</p>
            <div className="grid grid-cols-2 gap-2 font-mono text-sm text-white bg-black/40 rounded-lg p-3 mb-3">
              {backupCodes.map((c) => <div key={c} data-testid={`backup-code-${c}`}>{c}</div>)}
            </div>
            <div className="flex gap-2">
              <button onClick={copyBackupCodes} className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs rounded-lg inline-flex items-center gap-1.5" data-testid="copy-backup-codes-btn"><Copy className="w-3.5 h-3.5" /> Copier</button>
              <button onClick={() => setBackupCodes(null)} className="px-3 py-1.5 bg-primary text-white text-xs font-bold rounded-lg" data-testid="backup-codes-done-btn">J'ai sauvegardé ces codes</button>
            </div>
          </div>
        ) : twoFaEnabled ? (
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-2 text-emerald-400 text-sm font-semibold" data-testid="2fa-status-enabled"><Check className="w-4 h-4" /> 2FA activée</span>
            {!showDisable ? (
              <button onClick={() => setShowDisable(true)} className="px-3 py-1.5 text-xs text-red-300 bg-red-500/10 hover:bg-red-500/20 border border-red-500/25 rounded-lg" data-testid="2fa-disable-open-btn">Désactiver</button>
            ) : (
              <div className="flex items-center gap-2">
                <input type="password" placeholder="Mot de passe" value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)}
                  className="px-3 py-1.5 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm w-40" data-testid="2fa-disable-password" />
                <button onClick={confirmDisable} disabled={disabling} className="px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white text-xs font-bold rounded-lg disabled:opacity-50" data-testid="2fa-disable-confirm-btn">
                  {disabling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Confirmer'}
                </button>
              </div>
            )}
          </div>
        ) : setupData ? (
          <div className="space-y-3" data-testid="2fa-setup-block">
            <p className="text-zinc-300 text-sm">1. Scannez ce QR code avec votre application d'authentification :</p>
            <img src={setupData.qr_code_base64} alt="QR Code 2FA" className="w-40 h-40 rounded-xl bg-white p-2" data-testid="2fa-qr-code" />
            <p className="text-zinc-500 text-xs">Ou entrez manuellement ce secret : <span className="font-mono text-zinc-300">{setupData.secret}</span></p>
            <p className="text-zinc-300 text-sm">2. Entrez le code à 6 chiffres généré :</p>
            <div className="flex gap-2">
              <input type="text" value={enableCode} onChange={(e) => setEnableCode(e.target.value)} placeholder="123456" maxLength={6}
                className="px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm w-32 text-center tracking-widest" data-testid="2fa-enable-code-input" />
              <button onClick={confirmEnable} disabled={enabling || enableCode.length !== 6} className="px-4 py-2 bg-gradient-to-r from-primary to-pink-500 text-white text-sm font-bold rounded-lg disabled:opacity-50" data-testid="2fa-enable-confirm-btn">
                {enabling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Activer'}
              </button>
              <button onClick={() => setSetupData(null)} className="px-3 py-2 text-zinc-500 hover:text-white text-sm" data-testid="2fa-setup-cancel-btn">Annuler</button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-2 text-zinc-500 text-sm" data-testid="2fa-status-disabled"><X className="w-4 h-4" /> 2FA non activée</span>
            <button onClick={startSetup} className="px-4 py-2 bg-gradient-to-r from-primary to-pink-500 text-white text-sm font-bold rounded-xl inline-flex items-center gap-2 hover:scale-105 transition-transform" data-testid="2fa-setup-start-btn">
              <QrCode className="w-4 h-4" /> Activer la 2FA
            </button>
          </div>
        )}
      </div>

      {/* Équipe */}
      <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <h3 className="flex items-center gap-2 font-bold text-white"><Users className="w-5 h-5 text-primary" /> Équipe (comptes admin)</h3>
          <button onClick={() => setShowAddMember(!showAddMember)} className="px-3 py-1.5 bg-primary text-white text-xs font-bold rounded-lg inline-flex items-center gap-1.5" data-testid="team-add-toggle-btn">
            <Plus className="w-3.5 h-3.5" /> Ajouter
          </button>
        </div>
        <p className="text-zinc-500 text-sm mb-4">Créez un compte pour un assistant coach sans partager votre mot de passe personnel.</p>

        {showAddMember && (
          <div className="bg-white/5 rounded-xl p-4 mb-4 space-y-2" data-testid="team-add-form">
            <input type="email" placeholder="Email" value={newMember.email} onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm" data-testid="team-add-email" />
            <input type="text" placeholder="Nom (optionnel)" value={newMember.name} onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm" data-testid="team-add-name" />
            <input type="password" placeholder="Mot de passe (8+ caractères)" value={newMember.password} onChange={(e) => setNewMember({ ...newMember, password: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm" data-testid="team-add-password" />
            <button onClick={addMember} disabled={addingMember} className="px-4 py-2 bg-gradient-to-r from-primary to-pink-500 text-white text-sm font-bold rounded-lg disabled:opacity-50" data-testid="team-add-submit-btn">
              {addingMember ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer le compte'}
            </button>
          </div>
        )}

        {team === null ? <Loader2 className="w-5 h-5 animate-spin text-primary" /> : (
          <div className="space-y-2">
            {team.map((m) => (
              <div key={m.email} className="flex items-center justify-between p-3 bg-white/5 rounded-lg" data-testid={`team-member-${m.email}`}>
                <div>
                  <span className="text-white text-sm font-semibold">{m.name || m.email}</span>
                  {m.name && <span className="text-zinc-500 text-xs ml-2">{m.email}</span>}
                </div>
                <button onClick={() => removeMember(m.email)} className="p-1.5 text-zinc-500 hover:text-red-400" data-testid={`team-remove-btn-${m.email}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Journal d'audit */}
      <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5">
        <h3 className="flex items-center gap-2 font-bold text-white mb-1"><ScrollText className="w-5 h-5 text-primary" /> Journal d'audit</h3>
        <p className="text-zinc-500 text-sm mb-4">Traçabilité des actions sensibles (suppression/anonymisation RGPD des comptes clients).</p>
        {auditLog === null ? <Loader2 className="w-5 h-5 animate-spin text-primary" /> : auditLog.length === 0 ? (
          <p className="text-zinc-500 text-sm">Aucune action enregistrée.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-log-table">
              <thead className="text-zinc-500 text-xs uppercase">
                <tr><th className="text-left px-2 py-2">Date</th><th className="text-left px-2 py-2">Action</th><th className="text-left px-2 py-2">Par</th><th className="text-left px-2 py-2">Motif</th></tr>
              </thead>
              <tbody>
                {auditLog.map((a) => (
                  <tr key={a.audit_id} className="border-t border-white/5 text-zinc-300" data-testid={`audit-log-row-${a.audit_id}`}>
                    <td className="px-2 py-2 whitespace-nowrap text-xs">{fmtDate(a.at)}</td>
                    <td className="px-2 py-2 text-xs font-mono">{a.action}</td>
                    <td className="px-2 py-2 text-xs">{a.admin_email}</td>
                    <td className="px-2 py-2 text-xs text-zinc-500">{a.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSecurityPanel;
